// Waste catalog as the ops app sees it: each organization's basket (what its
// pickers offer), material types (basket rows shaped as MaterialTypeSchema)
// and the /waste-catalog/options tree.

import type { MaterialTypeSchema } from '../../types/recycling.type';
import {
  DEMO_CATEGORIES,
  DEMO_MATERIALS,
  DEMO_OUTLET_ORG_ID,
  DEMO_RECYCLER_ORG_ID,
  OUTLET_BASKET_MATERIAL_IDS,
  RECYCLER_BASKET_MATERIAL_IDS,
  categoryByCode,
  daysAgo,
  iso,
  materialById,
} from '../core';
import { nowIso, tr } from './common';

export interface BasketRow {
  material_id: string;
  client_custom_id: string | null;
  added_at: string;
}

let baskets: Record<string, BasketRow[]> = {};

function initialBasket(materialIds: string[], prefix: string): BasketRow[] {
  return materialIds.map((material_id, i) => ({
    material_id,
    client_custom_id: `${prefix}-${String(i + 1).padStart(3, '0')}`,
    added_at: iso(daysAgo(420 - i * 9)),
  }));
}

export function resetCatalog(): void {
  baskets = {
    [DEMO_OUTLET_ORG_ID]: initialBasket(OUTLET_BASKET_MATERIAL_IDS, 'NKH'),
    [DEMO_RECYCLER_ORG_ID]: initialBasket(RECYCLER_BASKET_MATERIAL_IDS, 'RGR'),
  };
}

export function basketFor(orgId: string | null | undefined): BasketRow[] {
  if (!orgId) return [];
  if (!baskets[orgId]) baskets[orgId] = [];
  return baskets[orgId];
}

export function materialName(materialId: string | null | undefined): string {
  const m = materialId ? materialById(materialId) : undefined;
  return m ? tr(m.name_en, m.name_ar) : materialId ?? '';
}

export function categoryName(code: string | null | undefined): string {
  const c = code ? categoryByCode(code) : undefined;
  return c ? tr(c.name_en, c.name_ar) : code ?? '';
}

/** Category code of a material, used as the pickup order's waste_type. */
export function wasteTypeOf(materialId: string | null | undefined): string {
  return (materialId ? materialById(materialId)?.category_code : undefined) ?? 'mixed';
}

export function toMaterialType(materialId: string, orgId: string | null): MaterialTypeSchema {
  const m = materialById(materialId);
  const row = orgId ? basketFor(orgId).find((r) => r.material_id === materialId) : undefined;
  const created = row?.added_at ?? iso(daysAgo(420));
  if (!m) {
    return {
      id: materialId,
      name: materialId,
      description: '',
      material_id: materialId,
      organization_id: orgId,
      parent_category: 'mixed',
      unit_of_measure: 'kg',
      extra_data: {},
      created_at: created,
      updated_at: created,
    };
  }
  const category = categoryByCode(m.category_code);
  return {
    id: m.id,
    name: tr(m.name_en, m.name_ar),
    description: `${category ? tr(category.name_en, category.name_ar) : m.category_code} · EWC ${m.ewc_codes.join(', ')}`,
    material_id: m.id,
    organization_id: orgId,
    parent_category: m.category_code,
    // Every demo stream is weighed at the facility, liquids included.
    unit_of_measure: 'kg',
    extra_data: {
      code: m.code,
      ewc_codes: m.ewc_codes.join(', '),
      physical_state: m.default_physical_state,
      hazardous: m.hazardous ? 'yes' : 'no',
      emission_factor_tco2e_per_t: m.emission_factor_tco2e_per_t,
      ...(row?.client_custom_id ? { client_custom_id: row.client_custom_id } : {}),
    },
    created_at: created,
    updated_at: created,
  };
}

export function listMaterialTypes(orgId: string, search?: string): MaterialTypeSchema[] {
  const rows = basketFor(orgId).map((r) => toMaterialType(r.material_id, orgId));
  if (!search) return rows;
  const q = search.toLowerCase();
  return rows.filter((r) => r.name.toLowerCase().includes(q) || (r.parent_category ?? '').includes(q));
}

export function organizationWasteTypes(orgId: string) {
  return basketFor(orgId).map((row) => {
    const m = materialById(row.material_id);
    return {
      material_id: row.material_id,
      name: m ? tr(m.name_en, m.name_ar) : row.material_id,
      code: m?.code ?? null,
      parent_category: m?.category_code ?? null,
      status: 'active',
      client_custom_id: row.client_custom_id,
    };
  });
}

export function addBasketMaterial(orgId: string, materialId: string, clientCustomId: string | null): BasketRow | 'conflict' | 'unknown' {
  if (!materialById(materialId)) return 'unknown';
  const basket = basketFor(orgId);
  if (basket.some((r) => r.material_id === materialId)) return 'conflict';
  const row: BasketRow = { material_id: materialId, client_custom_id: clientCustomId, added_at: nowIso() };
  basket.push(row);
  return row;
}

export function updateBasketMaterial(orgId: string, materialId: string, clientCustomId: string | null): BasketRow | null {
  const row = basketFor(orgId).find((r) => r.material_id === materialId);
  if (!row) return null;
  row.client_custom_id = clientCustomId;
  return row;
}

export function removeBasketMaterial(orgId: string, materialId: string): boolean {
  const basket = basketFor(orgId);
  const idx = basket.findIndex((r) => r.material_id === materialId);
  if (idx === -1) return false;
  basket.splice(idx, 1);
  return true;
}

export function replaceBasket(orgId: string, materialIds: string[]): void {
  const existing = new Map(basketFor(orgId).map((r) => [r.material_id, r]));
  baskets[orgId] = materialIds
    .filter((id) => materialById(id))
    .map((id) => existing.get(id) ?? { material_id: id, client_custom_id: null, added_at: nowIso() });
}

/**
 * GET /waste-catalog/options. With organization_id (and no include_all_materials)
 * only that organization's basket; otherwise the whole published catalog.
 */
export function catalogOptions(orgId: string | undefined, includeAll: boolean) {
  const allowed = orgId && !includeAll ? new Set(basketFor(orgId).map((r) => r.material_id)) : null;
  const categories = DEMO_CATEGORIES.map((c) => ({
    id: c.id,
    code: c.code,
    name: tr(c.name_en, c.name_ar),
    sort_order: c.sort_order,
    materials: DEMO_MATERIALS.filter((m) => m.category_code === c.code && (!allowed || allowed.has(m.id))).map((m) => ({
      id: m.id,
      code: m.code,
      name: tr(m.name_en, m.name_ar),
      selectable: true,
      status: 'active',
      hazardous: m.hazardous,
      physical_state: m.default_physical_state,
      ewc_codes: m.ewc_codes,
    })),
  })).filter((c) => c.materials.length > 0);
  return { context: 'ops', organization_id: orgId ?? null, categories };
}
